import { useEffect, useRef, useState } from 'react'

const fieldClass =
  'rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 shadow-sm outline-none focus:border-gray-400 focus:ring-2 focus:ring-gray-100'

const btnPrimary =
  'inline-flex items-center rounded-lg bg-gray-900 px-3.5 py-2 text-sm font-medium text-white shadow-sm transition hover:bg-gray-800 disabled:cursor-not-allowed disabled:opacity-60'

const btnSecondary =
  'rounded-lg border border-gray-300 bg-white px-3.5 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50'

function seedValues(fields, initialValues = {}) {
  const seed = {}
  for (const f of fields) {
    if (f.type === 'checkbox') seed[f.name] = Boolean(initialValues[f.name])
    else seed[f.name] = initialValues[f.name] ?? f.defaultValue ?? ''
  }
  return seed
}

/**
 * Modal create/upsert form for Artist / Sound / Snoop mapping pages.
 *
 * fields: Array<{
 *   name, label, type?: 'text'|'select'|'checkbox',
 *   options?: {value,label}[], placeholder?, required?, defaultValue?
 * }>
 * onSubmit(values) => Promise
 */
export default function MappingAddModal({
  open,
  onClose,
  title = 'Add mapping',
  fields,
  submitLabel = 'Save',
  onSubmit,
  initialValues = {},
}) {
  const [values, setValues] = useState(() => seedValues(fields, initialValues))
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)
  const wasOpen = useRef(false)
  const fieldsRef = useRef(fields)
  const initialRef = useRef(initialValues)
  fieldsRef.current = fields
  initialRef.current = initialValues

  useEffect(() => {
    if (open && !wasOpen.current) {
      setValues(seedValues(fieldsRef.current, initialRef.current))
      setError(null)
      setSaving(false)
    }
    wasOpen.current = open
    if (!open) return undefined

    function onKey(event) {
      if (event.key === 'Escape') onClose?.()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open, onClose])

  if (!open) return null

  function setField(name, value) {
    setValues((prev) => ({ ...prev, [name]: value }))
    setError(null)
  }

  async function handleSubmit(event) {
    event.preventDefault()
    for (const f of fields) {
      if (f.required && f.type !== 'checkbox') {
        const v = String(values[f.name] ?? '').trim()
        if (!v) {
          setError(`${f.label} is required`)
          return
        }
      }
    }
    setSaving(true)
    setError(null)
    try {
      await onSubmit(values)
      onClose?.()
    } catch (err) {
      setError(err?.message || 'Save failed')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div
      className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/40 p-4"
      onClick={() => {
        if (!saving) onClose?.()
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label={title}
        className="max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-xl border border-gray-200 bg-white p-5 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-4 flex items-start justify-between gap-3">
          <h2 className="text-base font-semibold text-gray-900">{title}</h2>
          <button
            type="button"
            onClick={() => {
              if (!saving) onClose?.()
            }}
            className="rounded-md px-2 py-1 text-sm text-gray-500 hover:bg-gray-100 hover:text-gray-800"
            aria-label="Close"
          >
            ✕
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            {fields.map((f) => {
              if (f.type === 'checkbox') {
                return (
                  <label
                    key={f.name}
                    className="flex items-center gap-2 text-sm text-gray-700 sm:col-span-2"
                  >
                    <input
                      type="checkbox"
                      checked={Boolean(values[f.name])}
                      onChange={(e) => setField(f.name, e.target.checked)}
                      className="h-4 w-4 rounded border-gray-300"
                    />
                    {f.label}
                  </label>
                )
              }
              if (f.type === 'select') {
                return (
                  <label
                    key={f.name}
                    className="flex flex-col gap-1 text-xs font-medium text-gray-600"
                  >
                    {f.label}
                    <select
                      className={fieldClass}
                      value={values[f.name] ?? ''}
                      onChange={(e) => setField(f.name, e.target.value)}
                      required={f.required}
                    >
                      {(f.options || []).map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </select>
                  </label>
                )
              }
              return (
                <label
                  key={f.name}
                  className="flex flex-col gap-1 text-xs font-medium text-gray-600"
                >
                  {f.label}
                  <input
                    type="text"
                    className={fieldClass}
                    value={values[f.name] ?? ''}
                    placeholder={f.placeholder || ''}
                    onChange={(e) => setField(f.name, e.target.value)}
                    required={f.required}
                  />
                </label>
              )
            })}
          </div>

          {error ? <p className="text-xs text-rose-700">{error}</p> : null}

          <div className="flex flex-wrap justify-end gap-2 pt-1">
            <button
              type="button"
              className={btnSecondary}
              disabled={saving}
              onClick={() => onClose?.()}
            >
              Cancel
            </button>
            <button type="submit" disabled={saving} className={btnPrimary}>
              {saving ? 'Saving…' : submitLabel}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
